import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import type { User } from '../../../types';
import { fetchUserById, searchChangeFormUsers } from '../../../utils/searchUsers';

const DEBOUNCE_MS = 320;

interface Props {
  id?: string;
  ariaLabel: string;
  value: number;
  onChange: (userId: number) => void;
  disabled?: boolean;
}

/**
 * Solicitante / dono com busca no servidor (GET /api/users/change-form-picker).
 */
const ChangeUserPickerInput: React.FC<Props> = ({ id, ariaLabel, value, onChange, disabled = false }) => {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<User[]>([]);
  const [selected, setSelected] = useState<User | null>(null);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [touched, setTouched] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const loadSelected = useCallback(async (userId: number, signal?: AbortSignal) => {
    if (!userId) {
      setSelected(null);
      return;
    }
    const user = await fetchUserById(userId, localStorage.getItem('token'), { signal });
    if (!signal?.aborted) setSelected(user);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void loadSelected(value, controller.signal);
    return () => controller.abort();
  }, [value, loadSelected]);

  const scheduleSearch = useCallback((query: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    abortRef.current?.abort();
    const trimmed = query.trim();
    if (!trimmed) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    timerRef.current = setTimeout(() => {
      const controller = new AbortController();
      abortRef.current = controller;
      void searchChangeFormUsers(trimmed, localStorage.getItem('token'), {
        limit: 30,
        signal: controller.signal,
      })
        .then((items) => {
          if (!controller.signal.aborted) {
            setResults(items);
            setSearching(false);
          }
        })
        .catch(() => {
          if (!controller.signal.aborted) {
            setResults([]);
            setSearching(false);
          }
        });
    }, DEBOUNCE_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      abortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const display =
    open || term ? term : selected ? `${selected.name} (${selected.email})` : '';

  const showList = open && term.trim().length > 0 && (results.length > 0 || searching);
  const awaitingSelection =
    touched && term.trim().length > 0 && !selected && !searching && results.length === 0;
  const invalid = awaitingSelection;

  return (
    <div className="position-relative" ref={wrapRef}>
      <Form.Control
        id={id}
        type="text"
        aria-label={ariaLabel}
        role="combobox"
        aria-expanded={showList}
        disabled={disabled}
        value={display}
        placeholder="Digite nome ou e-mail"
        isInvalid={invalid}
        onChange={(e) => {
          const v = e.target.value;
          setTerm(v);
          setOpen(true);
          if (!v.trim()) {
            onChange(0);
            setResults([]);
            return;
          }
          scheduleSearch(v);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTouched(true)}
      />
      {showList ? (
        <div
          className="border rounded-bottom shadow-sm bg-white"
          style={{ maxHeight: 220, overflowY: 'auto', zIndex: 20, position: 'absolute', width: '100%' }}
          role="listbox"
        >
          {results.map((user) => (
            <button
              key={user.id}
              type="button"
              role="option"
              className="btn btn-link text-start text-decoration-none text-dark w-100 py-2 px-3 border-bottom rounded-0"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange(user.id);
                setSelected(user);
                setTerm('');
                setResults([]);
                setOpen(false);
              }}
            >
              {user.name} ({user.email})
            </button>
          ))}
        </div>
      ) : null}
      {invalid ? (
        <Form.Text className="text-danger d-block">Selecione um usuário da lista.</Form.Text>
      ) : null}
    </div>
  );
};

export default ChangeUserPickerInput;
